import {
  useWorkspace,
  type AccountBootstrapHooks,
  type BootstrapIdentifyContext,
} from '@/features/account'
import { consumeAuthAttribution } from '@/features/auth/service/authAttribution'
import { useEnv } from '@/shared/config'
import { detectInitialLocale, setLocale } from '@/shared/i18n'
import { pushToDataLayer } from '@/shared/lib/analytics/gtm'
import { identifyIntercom } from '@/shared/lib/analytics/intercom'

// Consumers for module 07's post-boot hooks (contract 07 §8). 07 owns the TRIGGER and deliberately
// implements none of these itself; only the composition root may hold both the flow's seam and the
// modules that answer it (04 `setLocale`, the router). Same shape and same reason as
// `useRouteAnalytics` / `useExportTelemetry`.
//
// `onUnauthorized` is NOT here: the shell's own `useBootstrap` wires it to `AuthLogoutFlow`, and a
// second producer would race it. The rest of 07's hooks have no consumer and are deliberately left
// unregistered rather than faked (ORP-20381):
//   • `trackLoginOrSignup` — now consumes the short-lived attribution written by module 06 and
//     publishes the v3-compatible Core event through the GTM seam (ORP-20407).
//   • `onPackageSignal` — ALL FOUR kinds, `pricing` included → ORP-20408. Wiring `pricing` to 11's
//     `requestPlanSelection()` was tried here and REVERTED: v3 raised a dismissible modal OVER the
//     app (`applyUserPackages`, v3 `AccountBootstrapFlow.ts:100-110`), while v4's
//     `PlanSelectionSurface` is a full takeover AppLayout renders INSTEAD of the route, and 11's
//     seam is documented as "callers publish intent" — today only an explicit `?set_plan`. Turning
//     it into an automatic boot-time gate for every account whose package does not resolve is a
//     product decision, not wiring. Measured: it put ~150 e2e journeys on the Pricing page, and
//     only the specs that set `accountDetail.package` stayed green.
//   • `onDeferredError` — the deferred-compositions best-effort failure has no reporter → ORP-20408.

/** v3 `bootIntercom`'s `getCompanyPlan` — a numeric `accountDetail.package` id → Intercom's
 *  free-text `company.plan` field. Intercom-only; not the app's own plan-display convention. */
const INTERCOM_PLAN_NAMES: Record<number, string> = {
  1: 'Start-Up',
  2: 'Growth',
  3: 'Pro',
  4: 'Enterprise',
  5: 'Ad Metrics Integrator',
  6: 'Amazon Only',
  7: 'Shopify',
  8: 'Start-Up',
  9: 'Growth',
  10: 'Pro',
  11: 'Enterprise',
  12: 'Creative Standard',
  17: 'Start-Up',
  18: 'Growth',
  19: 'Pro',
  20: 'Enterprise',
  10000049: 'Start-Up',
  10000050: 'Growth',
  10000051: 'Pro',
  10000053: 'Enterprise',
  10000063: 'Creative Premium',
  10000064: 'Agentic Workflow',
  10000065: 'Criteo Enterprise',
}

function intercomPlanName(rawPackage: number | number[] | null | undefined): string {
  const id = Array.isArray(rawPackage) ? rawPackage[0] : rawPackage
  return (id !== undefined && id !== null && INTERCOM_PLAN_NAMES[id]) || 'Free'
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

/** v3 `/compositions/:id?` — the eager-vs-deferred compositions seam (`AccountBootstrapFlow`
 *  `isCompositionsRoute`). Read from the LANDING URL rather than the router on purpose: the
 *  coordinator installs before `app.use(router)`, so `router.currentRoute` is still
 *  `START_LOCATION` when the token watcher fires synchronously on a cold boot. A post-login boot
 *  is still on the auth surface at that moment, where deferred is the correct answer anyway. */
export function isCompositionsLanding(): boolean {
  return /^\/compositions(?:\/|$)/.test(window.location.pathname)
}

export interface AccountBootstrapHookOptions {
  navigate?: (target: { name: string }) => Promise<unknown> | void
  loadPendingInvites?: () => Promise<unknown[]>
}

export function useAccountBootstrapHooks(
  options: AccountBootstrapHookOptions = {},
): Omit<AccountBootstrapHooks, 'onUnauthorized'> {
  return {
    /** v3 `bootIntercom` — pushes the visitor's identity once account + accountDetail land
     *  (timing parity: `AccountBootstrapFlow.ts` fires this after both resolve, before workspace
     *  hydration). `identifyIntercom` itself no-ops without `INTERCOM_APP_ID` installed. */
    identify: ({ user, account, accountDetail }) => {
      identifyIntercom({
        userId: user?.user?.id !== undefined ? String(user.user.id) : undefined,
        email: asNonEmptyString(user?.user?.email) ?? asNonEmptyString(user?.email),
        name: asNonEmptyString(user?.user?.name),
        userJwt: asNonEmptyString(user?.intercom_token),
        company:
          account?.id !== undefined
            ? {
                id: String(account.id),
                name: asNonEmptyString(account.name),
                plan: intercomPlanName(accountDetail?.package),
                size: accountDetail?.connected_user_count,
              }
            : undefined,
      })
    },

    /** v3 `trackLoginOrSignup` — consume auth attribution only after account identity is ready. */
    trackLoginOrSignup: ({ user }: BootstrapIdentifyContext) => {
      const attribution = consumeAuthAttribution()
      if (!attribution) return
      const env = useEnv()
      if (env.MODE !== 'production' || !env.GTM_ID) return

      const userId = user?.user?.id ?? user?.id
      const email = user?.user?.email ?? user?.email
      const now = new Date().toISOString()
      const common = {
        user_id: userId !== undefined ? String(userId) : null,
        email: email ?? null,
        event_type: 'Core',
        created_at: now,
        front_url: env.API_APP_FRONT_URL ?? null,
      }

      if (attribution.kind === 'signup') {
        pushToDataLayer({
          ...common,
          event: 'user_signed_up',
          signup_method: attribution.method,
        })
        return
      }

      pushToDataLayer({
        ...common,
        event: 'user_logged_in',
        login_method: attribution.method,
        last_login_time: now,
      })
    },

    /** Workspace `language` attribute → 04's single locale-mutation path. `detectInitialLocale`
     *  keeps the contract-04 §8 order, so an explicit persisted user choice still outranks the
     *  workspace's language. A chunk-load failure keeps the current locale and must never fail
     *  the boot, so the rejection is swallowed here rather than bubbling into the flow. */
    syncWorkspaceLocale: () => {
      const language = useWorkspace().getAttribute('language')?.value
      const next = detectInitialLocale({
        workspaceLanguage: typeof language === 'string' ? language : null,
      })
      void setLocale(next).catch(() => {
        /* LocaleChunkError — 04 leaves the active locale untouched; the app stays usable. */
      })
    },

    /** Contract 07 §8 parity: "sysadmin → AdminDashboard on no-account". Module 19 is not being
     *  built in v4 (product decision 2026-07-31 — `/admin/*` is a PERMANENT legacy ingress), so
     *  the destination is v3's admin origin, reached the same way `AppTopbar`'s Admin Panel button
     *  reaches it: a real address transition, and only where the host is configured. Without one
     *  this deployment has no admin panel to send anyone to, so the sysadmin falls through to the
     *  same empty/onboarding state as everyone else — the boot already reaches it by marking
     *  `ready`. */
    onNoAccessibleAccount: async ({ isSysAdmin }) => {
      if (!isSysAdmin) {
        if (!options.navigate || !options.loadPendingInvites) return
        const invites = await options.loadPendingInvites()
        if (invites.length > 0) await options.navigate({ name: 'PendingInvites' })
        return
      }
      const adminUrl = useEnv().API_ADMIN_URL
      if (adminUrl !== undefined) window.location.assign(adminUrl)
    },
  }
}
